<template>
  <div class="container d-flex justify-content-center align-items-center mt-4">
    <div class="logo-container">
      <img
        class="img-fluid img-size-control"
        src="@/assets/ft-logo-login.png"
        width="300"
        alt="Falcon - Soluções Inteligentes"
      />
    </div>
    <div class="login">
      <form @submit.prevent="trocarSenha">
        <div class="row mb-3">
          <h1 class="h1-bem-vindo poppins-light">
            NOVA
            <span class="poppins-semibold">SENHA</span>
          </h1>
          <h2 class="h2-slogan poppins-light">
            Defina uma nova senha segura para acessar o sistema.
          </h2>
        </div>

        <div v-if="!modoReset" class="row">
          <div class="form-group">
            <label for="senha_atual" class="label-login">Senha atual</label>
            <input
              type="password"
              class="form-control"
              id="senha_atual"
              v-model="form.senha_atual"
              :placeholder="user?.force_password_change ? '••••••••' : ''"
              :disabled="user?.force_password_change"
              :required="!user?.force_password_change"
            />
          </div>
        </div>

        <div class="row">
          <div class="form-group">
            <label for="nova_senha" class="label-login">Nova senha</label>
            <input
              type="password"
              class="form-control"
              id="nova_senha"
              v-model="form.nova_senha"
              required
            />
          </div>
        </div>

        <div class="row">
          <div class="form-group">
            <label for="confirmar_senha" class="label-login">Confirmar nova senha</label>
            <input
              type="password"
              class="form-control"
              id="confirmar_senha"
              v-model="form.confirmar_senha"
              required
            />
          </div>
        </div>

        <div class="row mb-2">
          <small class="text-muted">
            Mínimo 8 caracteres, com letra maiúscula, minúscula e caractere especial.
          </small>
        </div>

        <div class="row">
          <div class="extand-login-button">
            <button
              v-if="modoReset"
              type="button"
              class="btn btn-outline-secondary"
              @click="$router.push('/login')"
              :disabled="loading"
            >
              Voltar ao login
            </button>
            <button
              v-else-if="user?.force_password_change"
              type="button"
              class="btn btn-outline-secondary"
              @click="sair"
              :disabled="loading"
            >
              Sair
            </button>
            <button
              v-else
              type="button"
              class="btn btn-outline-secondary"
              @click="voltar"
              :disabled="loading"
            >
              Voltar
            </button>
            <button type="submit" class="btn btn-secondary" :disabled="loading || (modoReset && !token)">
              {{ loading ? 'Salvando...' : 'Salvar nova senha' }}
            </button>
          </div>
        </div>
      </form>

      <div v-if="error" class="row mt-3">
        <div class="col-md-12">
          <div class="alert alert-danger fade show" role="alert">
            {{ error }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <Footer />
</template>

<script>
import axiosApi from "@/axios";
import { mapActions, mapState } from "vuex";
import Footer from "@/components/Footer.vue";
import { validarSenha } from "@/utils/senha";

export default {
  name: "TrocarSenha",
  components: { Footer },
  data() {
    return {
      form: {
        senha_atual: "",
        nova_senha: "",
        confirmar_senha: "",
      },
      loading: false,
      error: "",
      token: "",
    };
  },
  computed: {
    ...mapState(["user"]),
    modoReset() {
      return this.$route.name === "RedefinirSenha";
    },
  },
  async created() {
    if (this.modoReset) {
      /*
        O token chega no fragmento do link do e-mail. Fica só em memória e a URL
        é limpa em seguida, para ele não sobrar na barra de endereço nem no histórico.
        Usamos history.replaceState em vez de this.$router.replace porque este
        último dispara o beforeEach de novo e pode reexecutar created(),
        derrubando this.token antes do usuário submeter o formulário.
      */
      this.token = new URLSearchParams(this.$route.hash.slice(1)).get("token") || "";
      window.history.replaceState(null, "", this.$route.path);
      if (!this.token) {
        this.error = 'Link inválido ou expirado. Solicite um novo em "Esqueci minha senha".';
      }
      return;
    }

    try {
      await this.validateToken();
    } catch {
      this.$router.push("/login");
    }
  },
  methods: {
    ...mapActions(["validateToken", "showToast"]),
    validarSenha,
    voltar() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push("/");
      }
    },
    async sair() {
      try {
        await axiosApi.post("/api/login/logout", {}, { withCredentials: true });
      } catch (err) {
        console.error("Erro ao sair:", err.response?.data || err.message);
      }
      this.$store.commit("clearAuthData");
      this.$router.push("/login");
    },
    async redefinirSenha() {
      this.loading = true;
      try {
        await axiosApi.post("/api/senha/redefinir", {
          token: this.token,
          nova_senha: this.form.nova_senha,
        });

        this.showToast({
          title: "Senha redefinida",
          message: "Entre com a nova senha.",
          type: "toast-success",
          headerClass: "toast-header-success",
        });

        this.$router.push("/login");
      } catch (err) {
        this.error = err.response?.data?.message || "Não foi possível redefinir a senha. Tente novamente.";
      } finally {
        this.loading = false;
      }
    },
    async trocarSenha() {
      this.error = "";

      if (this.form.nova_senha !== this.form.confirmar_senha) {
        this.error = "A nova senha e a confirmação não coincidem.";
        return;
      }

      if (!this.validarSenha(this.form.nova_senha)) {
        this.error = "A nova senha deve ter no mínimo 8 caracteres, incluindo letra maiúscula, minúscula e caractere especial.";
        return;
      }

      if (this.modoReset) {
        await this.redefinirSenha();
        return;
      }

      if (!this.user?.email) {
        this.$router.push("/login");
        return;
      }

      this.loading = true;
      try {
        const payload = { nova_senha: this.form.nova_senha };
        if (!this.user?.force_password_change) {
          payload.senha_atual = this.form.senha_atual;
        }

        await axiosApi.post(`/api/users/${this.user.email}/change-password`, payload);
        await this.validateToken();

        this.showToast({
          title: "Senha alterada",
          message: "Sua senha foi alterada com sucesso.",
          type: "toast-success",
          headerClass: "toast-header-success",
        });

        this.$router.push("/");
      } catch (err) {
        this.error = err.response?.data?.message || "Não foi possível alterar a senha. Tente novamente.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
